import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Home from './components/home.component/home.component';
import Header from './components/header.component/header.component';
import About from './components/about.component/about.component';
import HowToPlay from './components/howToPlay.component/howToPlay.component';
import ContactUs from './components/contactUs.component/contactUs.component';
import Footer from './components/footer.component/footer.component';
import Terms from './components/terms.component/terms.component';
import Privacy from './components/privacy.component/privacy.component';
import Game from './components/game.component/Game.component';
import Login from './components/login.component/Login.component';
import Signup from './components/signup.component/Signup.component';
import EmailVerification from './components/emailVerification.component/EmailVerification.component';
import ForgotPassword from './components/forgotPassword.component/ForgotPassword.component';
import Dashboard from './components/dashboard.component/Dashboard.component';
import Settings from './components/settings.component/Settings.component';
import Lobby from './components/lobby.component/Lobby.component';
import { HelmetProvider } from 'react-helmet-async';

// Protected route component
const ProtectedRoute: React.FC<{ component: React.ComponentType }> = ({ component: Component }) => {
  // Use state to force re-render when localStorage changes
  const [isAuthenticated, setIsAuthenticated] = React.useState(() => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    const username = localStorage.getItem('username');
    return !!(token && userId && username);
  });

  // Check authentication on mount and when storage changes
  React.useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const username = localStorage.getItem('username');
      setIsAuthenticated(!!(token && userId && username));
    };

    // Check immediately
    checkAuth();

    // Listen for storage changes (in case auth happens in another tab/window)
    window.addEventListener('storage', checkAuth);

    // Also check periodically in case localStorage was set in the same window
    const interval = setInterval(checkAuth, 100);

    return () => {
      window.removeEventListener('storage', checkAuth);
      clearInterval(interval);
    };
  }, []);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Component />;
};

// Wrapper so redirect-only-if-logged-in runs in a stable component (avoids "Maximum update depth" from inline IIFE)
const LoginRoute: React.FC = () => {
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  if (token && userId) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Login />;
};

const SignupRoute: React.FC = () => {
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  if (token && userId) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Signup />;
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <Routes>
          <Route path="/dashboard" element={<ProtectedRoute component={Dashboard} />} />
          <Route path="/settings" element={<ProtectedRoute component={Settings} />} />
          <Route path="/game" element={<ProtectedRoute component={Game} />} />
          <Route path="/lobby" element={<ProtectedRoute component={Lobby} />} />
          <Route path="/" element={<>
            <Header />
            <Home />
            <Footer />
          </>
          } />
          <Route path="/about" element={
            <>
              <Header />
              <About />
              <Footer />
            </>
          } />
          <Route path="/how-to-play" element={
            <>
              <Header />
              <HowToPlay />
              <Footer />
            </>
          } />
          <Route path="/contact-us" element={
            <>
              <Header />
              <ContactUs />
              <Footer />
            </>
          } />
          <Route path="/terms" element={
            <>
              <Header />
              <Terms />
              <Footer />
            </>
          } />
          <Route path="/privacy" element={
            <>
              <Header />
              <Privacy />
              <Footer />
            </>
          } />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/signup" element={<SignupRoute />} />
        </Routes>
      </Router>
    </HelmetProvider>
  );
}

export default App;
